import React from 'react';
import { cn } from '@skybeam/shared-utils/classnames/cn';

import type {
  ImagePosition,
  ResponsivePadding,
  ResponsiveValue,
} from './types';

/**
 * Breakpoint values for responsive design.
 */
const BREAKPOINTS = {
  tablet: '568px',
  laptop: '960px',
  desktop: '1440px',
} as const;

/**
 * Generates a unique ID for a responsive style element based on its content.
 */
const generateStyleId = (content: unknown): string => {
  const hash = JSON.stringify(content)
    .split('')
    .reduce((acc, char) => ((acc << 5) - acc + char.charCodeAt(0)) | 0, 0);
  return `responsive-${Math.abs(hash)}`;
};

/**
 * Creates a style tag React element with the given CSS and ID.
 */
const createStyleTag = (id: string, css: string): React.ReactElement | null => {
  if (!css.trim()) return null;
  return React.createElement('style', {
    key: id,
    dangerouslySetInnerHTML: { __html: css },
  });
};

/**
 * Builds a media query CSS rule.
 */
const buildMediaQuery = (
  breakpoint: string,
  className: string,
  rules: string[],
): string => {
  if (rules.length === 0) return '';
  return `@media (min-width: ${breakpoint}) { .${className} { ${rules.join(' ')} } }`;
};

/**
 * Gets a style tag and className for responsive padding.
 *
 * @param {ResponsivePadding} padding - Responsive padding object from Sanity
 * @returns {Object} Object with `styleTag` (React element) and `className` (string)
 */
export const getResponsivePaddingStyleTag = (
  padding?: ResponsivePadding,
): { className: string; styleTag: React.ReactElement | null } => {
  if (!padding) return { styleTag: null, className: '' };

  const id = generateStyleId(padding);
  const className = `responsive-padding-${id}`;

  const buildPaddingRules = (p: {
    paddingBottom?: string;
    paddingLeft?: string;
    paddingRight?: string;
    paddingTop?: string;
  }): string[] => {
    const rules: string[] = [];
    if (p.paddingTop) rules.push(`padding-top: ${p.paddingTop};`);
    if (p.paddingRight) rules.push(`padding-right: ${p.paddingRight};`);
    if (p.paddingBottom) rules.push(`padding-bottom: ${p.paddingBottom};`);
    if (p.paddingLeft) rules.push(`padding-left: ${p.paddingLeft};`);
    return rules;
  };

  const cssParts: string[] = [
    `.${className} {`,
    `padding-top: ${padding.mobile?.paddingTop || '0'};`,
    `padding-right: ${padding.mobile?.paddingRight || '0'};`,
    `padding-bottom: ${padding.mobile?.paddingBottom || '0'};`,
    `padding-left: ${padding.mobile?.paddingLeft || '0'};`,
    '}',
  ];

  const tabletQuery = buildMediaQuery(
    BREAKPOINTS.tablet,
    className,
    buildPaddingRules(padding.tablet || {}),
  );
  if (tabletQuery) cssParts.push(tabletQuery);

  const laptopQuery = buildMediaQuery(
    BREAKPOINTS.laptop,
    className,
    buildPaddingRules(padding.laptop || {}),
  );
  if (laptopQuery) cssParts.push(laptopQuery);

  const desktopQuery = buildMediaQuery(
    BREAKPOINTS.desktop,
    className,
    buildPaddingRules(padding.desktop || {}),
  );
  if (desktopQuery) cssParts.push(desktopQuery);

  const css = cssParts.join(' ');

  return {
    styleTag: createStyleTag(id, css),
    className,
  };
};

/**
 * Gets a style tag and className for a responsive CSS property value.
 *
 * @param {ResponsiveValue} value - Responsive value object from Sanity
 * @param {string} cssProperty - CSS property name (e.g., 'gap', 'max-width', 'margin-top', 'row-gap')
 * @returns {Object} Object with `styleTag` (React element) and `className` (string)
 */
export const getResponsiveValueStyleTag = (
  value?: ResponsiveValue,
  cssProperty = 'gap',
): { className: string; styleTag: React.ReactElement | null } => {
  if (!value) return { styleTag: null, className: '' };

  const id = generateStyleId({ cssProperty, value });
  const className = `responsive-value-${id}`;

  const cssParts: string[] = [];

  if (value.mobile) {
    cssParts.push(`.${className} { ${cssProperty}: ${value.mobile}; }`);
  }

  const tabletQuery = buildMediaQuery(
    BREAKPOINTS.tablet,
    className,
    value.tablet ? [`${cssProperty}: ${value.tablet};`] : [],
  );
  if (tabletQuery) cssParts.push(tabletQuery);

  const laptopQuery = buildMediaQuery(
    BREAKPOINTS.laptop,
    className,
    value.laptop ? [`${cssProperty}: ${value.laptop};`] : [],
  );
  if (laptopQuery) cssParts.push(laptopQuery);

  const desktopQuery = buildMediaQuery(
    BREAKPOINTS.desktop,
    className,
    value.desktop ? [`${cssProperty}: ${value.desktop};`] : [],
  );
  if (desktopQuery) cssParts.push(desktopQuery);

  if (cssParts.length === 0) return { styleTag: null, className: '' };

  const css = cssParts.join(' ');

  return {
    styleTag: createStyleTag(id, css),
    className,
  };
};

/**
 * Gets style tags and className for responsive margin (top and bottom).
 *
 * @param {Object} margin - Object with marginTop and marginBottom ResponsiveValue objects
 * @returns {Object} Object with `styleTags` (array of React elements) and `className` (string)
 */
export const getResponsiveMarginStyleTag = (margin?: {
  marginBottom?: ResponsiveValue;
  marginTop?: ResponsiveValue;
}): { className: string; styleTags: React.ReactElement[] } => {
  if (!margin) return { styleTags: [], className: '' };

  const topStyle = getResponsiveValueStyleTag(margin.marginTop, 'margin-top');
  const bottomStyle = getResponsiveValueStyleTag(
    margin.marginBottom,
    'margin-bottom',
  );

  const styleTags = [topStyle.styleTag, bottomStyle.styleTag].filter(
    (tag): tag is React.ReactElement => tag !== null,
  );

  const className = cn(topStyle.className, bottomStyle.className);

  return { className, styleTags };
};

/**
 * Gets a style tag and className for responsive image position.
 *
 * @param {ImagePosition} imagePosition - Image position object with top, right, and maxWidth for each breakpoint
 * @returns {Object} Object with `styleTag` (React element) and `className` (string)
 */
export const getImagePositionStyleTag = (
  imagePosition?: ImagePosition,
): { className: string; styleTag: React.ReactElement | null } => {
  if (!imagePosition) return { styleTag: null, className: '' };

  const id = generateStyleId(imagePosition);
  const className = `image-position-${id}`;

  const buildPositionRules = (p: {
    maxWidth?: string;
    right?: string;
    top?: string;
  }): string[] => {
    const rules: string[] = [];
    if (p.top) rules.push(`top: ${p.top};`);
    if (p.right) rules.push(`right: ${p.right};`);
    if (p.maxWidth) rules.push(`max-width: ${p.maxWidth};`);
    return rules;
  };

  const cssParts: string[] = [];

  if (imagePosition.mobile) {
    const mobileRules = buildPositionRules(imagePosition.mobile);
    if (mobileRules.length > 0) {
      cssParts.push(`.${className} { ${mobileRules.join(' ')} }`);
    }
  }

  const tabletQuery = buildMediaQuery(
    BREAKPOINTS.tablet,
    className,
    buildPositionRules(imagePosition.tablet || {}),
  );
  if (tabletQuery) cssParts.push(tabletQuery);

  const laptopQuery = buildMediaQuery(
    BREAKPOINTS.laptop,
    className,
    buildPositionRules(imagePosition.laptop || {}),
  );
  if (laptopQuery) cssParts.push(laptopQuery);

  const desktopQuery = buildMediaQuery(
    BREAKPOINTS.desktop,
    className,
    buildPositionRules(imagePosition.desktop || {}),
  );
  if (desktopQuery) cssParts.push(desktopQuery);

  if (cssParts.length === 0) return { styleTag: null, className: '' };

  const css = cssParts.join(' ');

  return {
    styleTag: createStyleTag(id, css),
    className,
  };
};

/**
 * Gets a style tag and className for responsive text styles.
 * Handles font-size, color, and optionally max-width.
 *
 * @param {Object} styles - Object with textSize, textColor, and optionally maxWidth for each breakpoint
 * @returns {Object} Object with `styleTag` (React element) and `className` (string)
 */
export const getTextStylesStyleTag = (styles?: {
  desktop?: {
    maxWidth?: string;
    textColor?: string;
    textSize?: string;
  };
  laptop?: {
    maxWidth?: string;
    textColor?: string;
    textSize?: string;
  };
  mobile?: {
    maxWidth?: string;
    textColor?: string;
    textSize?: string;
  };
  tablet?: {
    maxWidth?: string;
    textColor?: string;
    textSize?: string;
  };
}): { className: string; styleTag: React.ReactElement | null } => {
  if (!styles) return { styleTag: null, className: '' };

  const id = generateStyleId(styles);
  const className = `text-styles-${id}`;

  const buildTextRules = (s: {
    maxWidth?: string;
    textColor?: string;
    textSize?: string;
  }): string[] => {
    const rules: string[] = [];
    if (s.textSize) rules.push(`font-size: ${s.textSize};`);
    if (s.textColor) rules.push(`color: ${s.textColor};`);
    if (s.maxWidth) rules.push(`max-width: ${s.maxWidth};`);
    return rules;
  };

  const cssParts: string[] = [];

  if (styles.mobile) {
    const mobileRules = buildTextRules(styles.mobile);
    if (mobileRules.length > 0) {
      cssParts.push(`.${className} { ${mobileRules.join(' ')} }`);
    }
  }

  const tabletQuery = buildMediaQuery(
    BREAKPOINTS.tablet,
    className,
    buildTextRules(styles.tablet || {}),
  );
  if (tabletQuery) cssParts.push(tabletQuery);

  const laptopQuery = buildMediaQuery(
    BREAKPOINTS.laptop,
    className,
    buildTextRules(styles.laptop || {}),
  );
  if (laptopQuery) cssParts.push(laptopQuery);

  const desktopQuery = buildMediaQuery(
    BREAKPOINTS.desktop,
    className,
    buildTextRules(styles.desktop || {}),
  );
  if (desktopQuery) cssParts.push(desktopQuery);

  if (cssParts.length === 0) return { styleTag: null, className: '' };

  const css = cssParts.join(' ');

  return {
    styleTag: createStyleTag(id, css),
    className,
  };
};

/**
 * Gets a style tag and className for responsive grid columns.
 *
 * @param {Object} gridColumns - Object with number of columns for each breakpoint
 * @returns {Object} Object with `styleTag` (React element) and `className` (string)
 */
export const getGridColumnsStyleTag = (gridColumns?: {
  desktop?: number;
  laptop?: number;
  mobile?: number;
  tablet?: number;
}): { className: string; styleTag: React.ReactElement | null } => {
  if (!gridColumns) return { styleTag: null, className: '' };

  const id = generateStyleId(gridColumns);
  const className = `grid-columns-${id}`;

  const gridColumnValue = (columns: number): string =>
    `grid-template-columns: repeat(${columns}, minmax(0, 1fr));`;

  const cssParts: string[] = [];

  if (gridColumns.mobile) {
    cssParts.push(`.${className} { ${gridColumnValue(gridColumns.mobile)} }`);
  }

  const tabletQuery = buildMediaQuery(
    BREAKPOINTS.tablet,
    className,
    gridColumns.tablet ? [gridColumnValue(gridColumns.tablet)] : [],
  );
  if (tabletQuery) cssParts.push(tabletQuery);

  const laptopQuery = buildMediaQuery(
    BREAKPOINTS.laptop,
    className,
    gridColumns.laptop ? [gridColumnValue(gridColumns.laptop)] : [],
  );
  if (laptopQuery) cssParts.push(laptopQuery);

  const desktopQuery = buildMediaQuery(
    BREAKPOINTS.desktop,
    className,
    gridColumns.desktop ? [gridColumnValue(gridColumns.desktop)] : [],
  );
  if (desktopQuery) cssParts.push(desktopQuery);

  if (cssParts.length === 0) return { styleTag: null, className: '' };

  const css = cssParts.join(' ');

  return {
    styleTag: createStyleTag(id, css),
    className,
  };
};
